import {ChangeDetectorRef, Component, ElementRef, Input, OnChanges, OnDestroy, OnInit, SimpleChanges, ViewChild} from "@angular/core";
import {InternalDocumentsService} from "./internal-documents.service";
import {NbPopoverDirective} from "@nebular/theme";
import {BehaviorSubject, combineLatest, Observable, Subject} from "rxjs";
import {filter, first, map, mergeMap} from "rxjs/operators";
import {AuthenticationService} from "../../../authentication/authentication.service";

@Component({
    selector: 'internal-documents',
    templateUrl: './internal-documents.template.html',
    styleUrls: ['./internal-documents.style.scss'],
    standalone: false
})
export class InternalDocumentsComponent implements OnInit, OnDestroy {

	public readonly AVAILABLE_UPLOAD_FORMATS = ".doc,.docx,application/msword,.pdf,.csv,.txt,.pages,.numbers,.xlsx,.xls,image/*," +
		"application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel";
	private readonly MB15: number = 15000000;
	private readonly MAX_FILES_NUMBER: number = 50;

	@Input() correlationId: string;
	@Input() blockInputs: boolean = false;

	@ViewChild(NbPopoverDirective, {static: false}) popover: NbPopoverDirective;
	@ViewChild('fileSelector', {static: false}) uploadElRef: ElementRef;

	private alive: boolean;

	files$: Subject<InternalDocument[]> = new BehaviorSubject([]);

	fileIsTooBig: boolean = false;
	showTooManyFiles$: Subject<boolean> = new BehaviorSubject(false);
	uploadingInProgress: boolean = false;
	uploadedComplete: boolean = false;

	constructor(private internalDocumentsService: InternalDocumentsService, private changeDet: ChangeDetectorRef, private authService: AuthenticationService) {
	}

	ngOnInit(): void {
		this.alive = true;
		this.internalDocumentsService.fetchInternalDocumentEntries(this.correlationId).subscribe(auditDocuments => this.files$.next(auditDocuments));
		this.files$.subscribe(() => this.triggerValidations());
	}

	ngOnDestroy(): void {
		this.alive = false;
	}

	upload(fileEvent: any) {
		this.fileIsTooBig = false;
		this.triggerValidations();
		const file: File = fileEvent.target.files[0] as File;
		if (file.size > this.MB15) {
			this.fileIsTooBig = true;
		}

		if (!this.fileIsTooBig) {
			combineLatest([this.showTooManyFiles$, this.fileIsNotDuplicated(file.name)]).pipe(
				first(),
				filter(([tooManyFiles, fileIsNotDuplicated]) => !tooManyFiles && fileIsNotDuplicated),
				mergeMap(() => {
					this.uploadingInProgress = true;
					return this.internalDocumentsService.uploadInternalDocument(this.correlationId, file);
				})
			).subscribe(entries => {
				this.files$.next(entries);
				this.uploadingInProgress = false;
				this.uploadSuccessfull();
			});
		}
		this.uploadElRef.nativeElement.value = ''
	}

	uploadSuccessfull() {
		this.uploadedComplete = true;
		setTimeout(() => {
			if (this.alive) {
				this.uploadedComplete = false;
				this.changeDet.detectChanges();
			}
		}, 5000);
	}

	triggerValidations() {
		this.files$.subscribe(files => {
			this.showTooManyFiles$.next(files.length >= this.MAX_FILES_NUMBER)
		});
	}

	delete(fileUuid: string) {
		this.internalDocumentsService.deleteInternalDocument(this.correlationId, fileUuid).subscribe(entries => {
			this.files$.next(entries);
		});
	}

	private fileIsNotDuplicated(fileName: string): Observable<boolean> {
		return this.files$.pipe(map(files => !files.some(f => f.deletionTimestamp == null && f.fileName == fileName)));
	}
}

export enum DocumentType {
	UPLOADED_FILE = 'UPLOADED_FILE',
	AR_CHECKLIST = 'AR_CHECKLIST',
	ACTIVE_ORDER = 'ACTIVE_ORDER'
}

export interface Author {
	firstName: string;
	lastName: string;
}

export interface InternalDocument {
	fileUuid: string;
	fileName: string;
	uploadTimestamp: Date;
	deletionTimestamp: Date;
	type: DocumentType;
	canDelete: boolean;
	uploadAuthor: Author;
	deletionAuthor: Author;
}
